import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { VolunteerTrainingPageMetaProps, Training } from '../data/types'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { LoadingSpinner, useAuth, VolunteerTrainingCard } from '../components'

type Props = {
    meta: VolunteerTrainingPageMetaProps
}


export const VolunteerTraining = (props: Props) => {
    const { meta } = props

    const { user } = useAuth()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [volunteerTrainings, setVolunteerTrainings] = useState<Training[]>([])
    const [filter, setFilter] = useState<"all" | "completed" | "in progress">("in progress");
    const navigate = useNavigate()

    const volunteerId = user?.volunteer_csid


    useEffect(() => {
        const fetchTrainings = async () => {
            setLoading(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/volunteer/${volunteerId}/training`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`)
                }

                const result = await response.json()
                setVolunteerTrainings(result)
            }
            catch (err) {
                setError(err instanceof Error ? err.message : "An error occurred while fetching trainings.")
            }
            finally {
                setLoading(false)
            }
        }

        if (volunteerId) {
            fetchTrainings()
        }
    }, [volunteerId])


    const handleOnViewClick = (training) => {
        navigate(`/volunteer/training/${training.id}/details`, { state: { training, volunteerId } })
    }

    const filteredTrainings = volunteerTrainings.filter((t) => {
        if (filter === "all") return true;
        if (filter === "completed") return t.completion_status === 1;
        if (filter === "in progress") return t.completion_status === 0 || t.completion_status === null;
        return true;
    });


    return (
        <div className="flex-1 pt-8 pb-8">
            {/*Content*/}
            <div className="container mx-auto px-4">

                {/*Title and Description*/}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                        {meta.title}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                        {meta.description}
                    </p>
                </div>

                {loading && (
                    <div className="flex justify-center items-center min-h-[400px]">
                        <LoadingSpinner meta={LoadingSpinnerMeta} />
                    </div>
                )}

                {error && (
                    <div className="max-w-2xl mx-auto">
                        <div
                            className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                            <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && volunteerTrainings.length === 0 && (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                        <p className="text-xl">{meta.noTrainingMessage}</p>
                    </div>
                )}

                {!loading && !error && volunteerTrainings.length > 0 && (
                    <>
                        <div className="flex justify-center mb-6">
                            <select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value as any)}
                                className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm"
                            >
                                <option value="all">All Trainings</option>
                                <option value="completed">Completed</option>
                                <option value="in progress">In Progress</option>
                            </select>
                        </div>

                        <div className="flex flex-wrap justify-center gap-8">
                            {filteredTrainings.map((training) => (
                                <VolunteerTrainingCard
                                    key={training.id}
                                    training={training}
                                    onClick={handleOnViewClick}
                                    buttonText={meta.viewButton}
                                />
                            ))}
                        </div>
                    </>

                )}


            </div>
        </div>
    )
}
